import { format } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { getSessionDate } from "./dateUtils";

interface SessionExportData {
  id: number;
  created_at_ts?: number | null;
  created_at?: string | null;
  createdAt?: string | null;
  score?: number | null;
  score_max?: number | null;
  total_session_duration?: number | null;
  total_session_completion_percentage?: number | null;
  is_completed?: boolean;
  is_succeeded?: boolean;
  user_name?: string;
  enduser?: {
    id?: number;
    username?: string;
    firstname?: string;
    lastname?: string;
    email?: string;
  } | null;
}

interface ExportOptions {
  filename?: string;
  locale?: "fr" | "en";
}

/**
 * Escape CSV value by wrapping in quotes if it contains special characters
 */
const escapeCsvValue = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return "";
  const stringValue = String(value);
  // If the value contains comma, newline, or double quote, wrap in quotes
  // eslint-disable-next-line quotes
  if (stringValue.includes(",") || stringValue.includes("\n") || stringValue.includes('"')) {
    // eslint-disable-next-line quotes
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

/**
 * Format duration in seconds to MM:SS format
 */
const formatDuration = (seconds: number | null | undefined): string => {
  if (seconds === null || seconds === undefined) return "";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

/**
 * Check if session has an authenticated user
 */
const isAuthenticated = (session: SessionExportData): boolean => {
  return !!session.enduser?.id;
};

/**
 * Export sessions data to CSV file
 */
export const exportSessionsToCsv = (
  sessions: SessionExportData[],
  options: ExportOptions = {},
): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      const { filename = "sessions-export", locale = "fr" } = options;
      const dateLocale = locale === "fr" ? fr : enUS;

      // Define headers based on locale
      const headers =
        locale === "fr"
          ? [
              "ID",
              "Date",
              "Heure",
              "Type",
              "Score",
              "Score Max",
              "Durée",
              "Complétion (%)",
              "Terminée",
              "Réussie",
            ]
          : [
              "ID",
              "Date",
              "Time",
              "Type",
              "Score",
              "Max Score",
              "Duration",
              "Completion (%)",
              "Completed",
              "Succeeded",
            ];

      // Build CSV content
      const rows = sessions.map((session) => {
        const date = getSessionDate(session);
        const userType = isAuthenticated(session)
          ? locale === "fr"
            ? "Authentifié"
            : "Authenticated"
          : locale === "fr"
          ? "Anonyme"
          : "Anonymous";
        const score = session.score != null && session.score >= 0 ? Math.round(session.score) : "";
        const scoreMax = session.score_max != null ? Math.round(session.score_max) : "";
        const duration = formatDuration(session.total_session_duration);
        const completion =
          session.total_session_completion_percentage != null
            ? Math.round(session.total_session_completion_percentage)
            : "";
        const isCompleted = session.is_completed
          ? locale === "fr"
            ? "Oui"
            : "Yes"
          : locale === "fr"
          ? "Non"
          : "No";
        const isSucceeded = session.is_succeeded
          ? locale === "fr"
            ? "Oui"
            : "Yes"
          : locale === "fr"
          ? "Non"
          : "No";

        return [
          session.id,
          format(date, "dd/MM/yyyy", { locale: dateLocale }),
          format(date, "HH:mm", { locale: dateLocale }),
          userType,
          score,
          scoreMax,
          duration,
          completion,
          isCompleted,
          isSucceeded,
        ].join(",");
      });

      // Add BOM for Excel compatibility with UTF-8
      const BOM = "\uFEFF";
      const csvContent = BOM + [headers.join(","), ...rows].join("\n");

      // Create blob and download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `${filename}-${format(new Date(), "yyyy-MM-dd")}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      resolve();
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Export learners data to CSV file
 */
interface LearnerExportData {
  id: number | string;
  firstName?: string;
  lastName?: string;
  email?: string;
  sessionCount: number;
  lastSessionDate: Date | null;
  lastScore: number | null;
  isAnonymous?: boolean;
}

/**
 * Export interaction data to CSV file
 */
interface InteractionExportData {
  id: number;
  content?: {
    id: number;
    name: string;
    type?: string;
  };
  score: number;
  scoreMax: number | null;
  time_start_content: number;
  time_end_content: number;
  specific_data?: Record<string, unknown>;
}

export const exportInteractionToCsv = (
  interaction: InteractionExportData,
  options: ExportOptions = {},
): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      const { filename = "interaction-export", locale = "fr" } = options;

      // Define headers based on locale
      const headers = locale === "fr" ? ["Champ", "Valeur"] : ["Field", "Value"];

      const duration = interaction.time_end_content - interaction.time_start_content;
      const durationFormatted = formatDuration(duration);

      // Build rows with interaction data
      const dataRows = [
        [locale === "fr" ? "Nom" : "Name", escapeCsvValue(interaction.content?.name || "-")],
        [locale === "fr" ? "Type" : "Type", escapeCsvValue(interaction.content?.type || "-")],
        ["Score", `${Math.round(interaction.score)}/${interaction.scoreMax || 1}`],
        [locale === "fr" ? "Durée" : "Duration", durationFormatted],
      ];

      // Add specific_data fields if present
      if (interaction.specific_data && typeof interaction.specific_data === "object") {
        dataRows.push([
          locale === "fr" ? "--- Données spécifiques ---" : "--- Specific Data ---",
          "",
        ]);
        const flattenObject = (obj: Record<string, unknown>, prefix = ""): [string, string][] => {
          const result: [string, string][] = [];
          for (const [key, value] of Object.entries(obj)) {
            const fullKey = prefix ? `${prefix}.${key}` : key;
            if (value && typeof value === "object" && !Array.isArray(value)) {
              result.push(...flattenObject(value as Record<string, unknown>, fullKey));
            } else if (Array.isArray(value)) {
              result.push([fullKey, escapeCsvValue(JSON.stringify(value))]);
            } else {
              result.push([fullKey, escapeCsvValue(String(value ?? ""))]);
            }
          }
          return result;
        };
        dataRows.push(...flattenObject(interaction.specific_data));
      }

      const rows = dataRows.map((row) => row.join(","));

      // Add BOM for Excel compatibility with UTF-8
      const BOM = "\uFEFF";
      const csvContent = BOM + [headers.join(","), ...rows].join("\n");

      // Create blob and download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const contentName = interaction.content?.name?.replace(/[^a-zA-Z0-9]/g, "-") || "interaction";
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `${filename}-${contentName}-${format(new Date(), "yyyy-MM-dd")}.csv`,
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      resolve();
    } catch (error) {
      reject(error);
    }
  });
};

export const exportLearnersToCsv = (
  learners: LearnerExportData[],
  options: ExportOptions = {},
): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      const { filename = "learners-export", locale = "fr" } = options;
      const dateLocale = locale === "fr" ? fr : enUS;

      // Define headers based on locale
      const headers =
        locale === "fr"
          ? [
              "Type",
              "Prénom",
              "Nom",
              "Email",
              "Nombre de sessions",
              "Dernière session",
              "Dernier score",
            ]
          : [
              "Type",
              "First Name",
              "Last Name",
              "Email",
              "Session Count",
              "Last Session",
              "Last Score",
            ];

      // Build CSV content
      const rows = learners.map((learner) => {
        const lastSession = learner.lastSessionDate
          ? format(learner.lastSessionDate, "dd/MM/yyyy HH:mm", { locale: dateLocale })
          : "";
        const lastScore = learner.lastScore != null ? Math.round(learner.lastScore) : "";
        const type = learner.isAnonymous
          ? locale === "fr"
            ? "Anonyme"
            : "Anonymous"
          : locale === "fr"
          ? "Authentifié"
          : "Authenticated";

        return [
          type,
          escapeCsvValue(learner.firstName || ""),
          escapeCsvValue(learner.lastName || ""),
          escapeCsvValue(learner.email || ""),
          learner.sessionCount,
          lastSession,
          lastScore,
        ].join(",");
      });

      // Add BOM for Excel compatibility with UTF-8
      const BOM = "\uFEFF";
      const csvContent = BOM + [headers.join(","), ...rows].join("\n");

      // Create blob and download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `${filename}-${format(new Date(), "yyyy-MM-dd")}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      resolve();
    } catch (error) {
      reject(error);
    }
  });
};
